import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {searchableAttrsFrom} from '../../../../../../srcCommon/inboxes/search/searchHelpers';
import {buildTeammateDisplayName} from '../../../../../helpers/modelHelpers';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {TeammateAvatar} from '../../../../common/avatars/teammateAvatar';
import {InteractiveDateTimeFormatterContainer} from '../../../../common/formatters/dateTime/interactiveDateTimeFormatterContainer';
import {
  StyledCommentContentDiv,
  StyledCommentDiv,
  StyledCommentHeaderTitleDiv,
  StyledCommentRow,
  StyledCommentTimestampDiv,
  StyledCommentTimestampWrapperDiv,
  StyledHeaderContentDiv,
  StyledHeaderRowDiv,
  StyledRestDiv,
} from './commentBase';

/*
 * Props.
 */

export interface CommentTombstoneProps {
  shouldHideTimestamp: boolean;
  shouldHideTitle: boolean;
  author: TeammateModel | undefined;
  date: number;
}

/*
 * Style.
 */

const StyledBubbleDiv = styled.div`
  display: grid;
  max-width: 100%;
  grid-auto-flow: column;
  line-height: 22px;
  padding: 3px 10px;
  user-select: text;
  cursor: text;

  background-color: ${(p) => p.theme.greys.shade10};
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  font-style: italic;
  letter-spacing: 0.1px;
  height: 100%;

  border-radius: 15px;
  border-color: ${(p) => p.theme.greys.shade40};
  border-style: solid;
  border-width: thin;
`;

const StyledIcon = styled(Icon)`
  margin-right: 4px;
  margin-top: 3px;
  margin-left: -2px;
`;

const StyledAvatarDiv = styled.div`
  grid-area: avatar;
`;

/*
 * Component.
 */

export const CommentTombstone: FC<CommentTombstoneProps> = (props) => {
  const theme = useUserTheme();
  const {shouldHideTimestamp, shouldHideTitle, author, date} = props;
  return (
    <StyledCommentDiv
      $shouldHideTitle={shouldHideTitle}
      $isPinned={false}
      $hasSameAuthorAsPrevious={false}
      $isReply={false}
      $isFirstComment={false}
    >
      <StyledCommentRow>
        {author && maybeRenderTitleWithAvatar(author, shouldHideTitle)}
        <StyledRestDiv>{maybeRenderTimestamp(date, shouldHideTimestamp)}</StyledRestDiv>
        <StyledCommentContentDiv>
          <StyledBubbleDiv>
            <StyledIcon name="noneSmall" color={theme.greys.shade70} />
            <Trans id="comment-tombstone-deleted">This comment was deleted</Trans>
          </StyledBubbleDiv>
        </StyledCommentContentDiv>
      </StyledCommentRow>
    </StyledCommentDiv>
  );
};

function maybeRenderTitleWithAvatar(author: TeammateModel, shouldHideTitle: boolean) {
  if (shouldHideTitle) {
    return null;
  }
  return (
    <>
      <StyledHeaderContentDiv>
        <StyledHeaderRowDiv {...searchableAttrsFrom}>
          <StyledCommentHeaderTitleDiv>{buildTeammateDisplayName(author)}</StyledCommentHeaderTitleDiv>
        </StyledHeaderRowDiv>
      </StyledHeaderContentDiv>
      <StyledAvatarDiv>
        <TeammateAvatar teammate={author} size={VisualSizesEnum.LARGE} />
      </StyledAvatarDiv>
    </>
  );
}

function maybeRenderTimestamp(date: number, shouldHideTimestamp: boolean) {
  return (
    <StyledCommentTimestampWrapperDiv $shouldHideTimestamp={shouldHideTimestamp}>
      <StyledCommentTimestampDiv>
        <InteractiveDateTimeFormatterContainer value={date} />
      </StyledCommentTimestampDiv>
    </StyledCommentTimestampWrapperDiv>
  );
}
